import { supabase } from '../lib/supabase';
import { log } from './logger';

/**
 * Session Manager - Handles session expiration detection and response
 */
class SessionManager {
  constructor() {
    this.listeners = new Set();
    this.isInitialized = false;
  }

  /**
   * Initialize the session manager
   */
  initialize() {
    if (this.isInitialized) return;

    this.setupGlobalErrorHandling();
    this.isInitialized = true;
    log.info('🔒 Session Manager initialized');
  }

  /**
   * Add a listener for session expiration events
   * @param {Function} listener - Function to call when session expires
   */
  addSessionExpiredListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove a session expiration listener
   * @param {Function} listener - Function to remove
   */
  removeSessionExpiredListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners that the session has expired
   */
  notifySessionExpired() {
    log.warn('🔒 Session expired - notifying listeners');
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        log.error('Error in session expired listener:', error);
      }
    });
  }

  /**
   * Check if an error indicates session expiration
   * @param {Error} error - The error to check
   * @returns {boolean} - True if the error indicates session expiration
   */
  isSessionExpiredError(error) {
    if (!error) return false;

    // Check for various session expiration indicators
    const message = error.message?.toLowerCase() || '';
    const code = error.code;
    const status = error.status;

    return (
      // JWT token errors
      message.includes('jwt expired') ||
      message.includes('invalid jwt') ||
      message.includes('jwt malformed') ||
      
      // Supabase specific errors
      code === 'invalid_grant' ||
      code === 'token_not_found' ||
      code === 'refresh_token_not_found' ||
      
      // HTTP status codes indicating auth issues
      status === 401 ||
      status === 403 ||
      
      // Supabase RLS errors (when session is invalid)
      (message.includes('row level security') && status === 401) ||
      
      // Generic session/auth related messages
      message.includes('session not found') ||
      message.includes('user not authenticated') ||
      message.includes('access denied')
    );
  }

  /**
   * Handle a potential session expiration error
   * @param {Error} error - The error to handle
   * @returns {boolean} - True if the error was handled as a session expiration
   */
  handlePotentialSessionExpiration(error) {
    if (this.isSessionExpiredError(error)) {
      log.warn('🔒 Detected session expiration error:', error.message);
      this.notifySessionExpired();
      return true;
    }
    return false;
  }

  /**
   * Setup global error handling to catch session expiration
   */
  setupGlobalErrorHandling() {
    // Handle unhandled promise rejections that might indicate session issues
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && this.isSessionExpiredError(event.reason)) {
        log.warn('🔒 Unhandled session expiration detected:', event.reason.message);
        this.notifySessionExpired();
        event.preventDefault(); // Prevent console error
      }
    });

    // Intercept fetch requests to catch 401/403 responses
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        
        // Check for auth-related HTTP status codes
        if (response.status === 401 || response.status === 403) {
          const url = args[0];
          // Only handle Supabase API calls (not third-party APIs)
          if (typeof url === 'string' && url.includes('supabase')) {
            log.warn('🔒 HTTP auth error detected:', response.status, url);
            this.notifySessionExpired();
          }
        }
        
        return response;
      } catch (error) {
        this.handlePotentialSessionExpiration(error);
        throw error;
      }
    };

    log.info('🔒 Global error handling setup complete');
  }

  /**
   * Manually check session validity
   * @returns {Promise<boolean>} - True if session is valid
   */
  async checkSessionValidity() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        log.warn('🔒 Session validation failed:', error?.message || 'no session');
        this.notifySessionExpired();
        return false;
      }
      
      // Additional check: try to make a simple authenticated request
      const { error: testError } = await supabase
        .from('users')
        .select('id')
        .limit(1);
        
      if (testError && this.isSessionExpiredError(testError)) {
        log.warn('🔒 Session test query failed:', testError.message);
        this.notifySessionExpired();
        return false;
      }
      
      return true;
    } catch (error) {
      log.error('🔒 Error checking session validity:', error);
      if (this.isSessionExpiredError(error)) {
        this.notifySessionExpired();
        return false;
      }
      return true; // If it's not a session error, assume session is still valid
    }
  }
}

// Create singleton instance
export const sessionManager = new SessionManager();

// Initialize when module is loaded
sessionManager.initialize();